import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/layout/Layout/Layout';
import apiClient from '../services/apiClient';
import Skeleton, { SkeletonText, SkeletonButton } from '../components/common/Skeleton/Skeleton';
import './TasksList.scss';
import goToExternal from '../utils/navigation';

const TasksList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const project = queryParams.get('project');
  // read initial q param (server-side search param)
  const initialSearch = queryParams.get('q') || '';

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(initialSearch);
  const loadTasks = useCallback(async (searchTerm = '') => {
    try {
      setLoading(true);
      const q = new URLSearchParams();
      if (project) q.set('project', project);
      if (searchTerm) q.set('q', searchTerm);
      const qs = q.toString();
      const response = await apiClient.get(`/api/tasks/${qs ? `?${qs}` : ''}`);
      setTasks(response.data.results || []);
    } catch (error) {
      console.error('Error al cargar tareas:', error);
    } finally {
      setLoading(false);
    }
  }, [project]);

  // Load tasks on mount and whenever `search` or `project` change.
  useEffect(() => {
    loadTasks(search);
  }, [loadTasks, search]);

  if (loading) {
    return (
      <Layout>
        <div className="tasks-list-container">
          <div className="container">
            <div className="tasks-header">
              <Skeleton width="200px" height="35px" borderRadius="8px" className="mb-8px" />
              <Skeleton width="350px" height="20px" borderRadius="6px" />
            </div>

            <div className="tasks-table">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="task-row">
                  <div className="task-content">
                    <Skeleton width="70%" height="22px" borderRadius="6px" className="mb-8px" />
                    <SkeletonText lines={2} width={['100%', '85%']} height="14px" spacing="8px" />
                    <div style={{ marginTop: '12px', display: 'flex', gap: '1.5rem' }}>
                      <Skeleton width="120px" height="16px" borderRadius="6px" />
                      <Skeleton width="150px" height="16px" borderRadius="6px" />
                    </div>
                  </div>
                  <SkeletonButton width="120px" height="38px" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="tasks-list-container">
        <div className="container">
          <div className="tasks-header">
            <h1>Todas las Tareas</h1>
            <p>Listado completo de tareas registradas</p>

            <div className="tasks-search-wrapper">
              <input
                type="search"
                className="tasks-search"
                placeholder="Buscar tareas por título o contacto..."
                value={search}
                onChange={(e) => {
                  const newSearch = e.target.value;
                  setSearch(newSearch);
                  // Update query string in URL using `q`
                  const params = new URLSearchParams(location.search);
                  if (newSearch) params.set('q', newSearch);
                  else params.delete('q');
                  // keep project param if present
                  if (project) params.set('project', project);
                  const qs = params.toString();
                  navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
                }}
                aria-label="Buscar tareas"
              />
            </div>
          </div>

          {tasks.length > 0 ? (
            <div className="tasks-table">
              {/** Server-side filtered tasks (render as returned by API) */}
              {tasks.map((task) => (
                 <div key={task.id} className="task-row">
                  <div className="task-content">
                    <h4>{task.title}</h4>
                    <p>
                      {task.description.length > 150 
                        ? task.description.substring(0, 150) + '...' 
                        : task.description}
                    </p>
                    <div className="task-meta">
                      <span className="task-id">ID: {task.id}</span>
                      {task.unread_chat_count > 0 && (
                        <span
                          className="task-id"
                          style={{
                            background: '#fee2e2',
                            color: '#991b1b',
                            borderRadius: '999px',
                            padding: '2px 8px',
                            fontWeight: 600
                          }}
                        >
                          Chat: {task.unread_chat_count}
                        </span>
                      )}
                      <span className="task-date">
                        {new Date(task.created_at).toLocaleDateString('es-AR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                  <button 
                    className="btn btn-primary"
                    onClick={() => navigate(`/admin/questions/task/incidentes/${task.id}`)}
                  >
                    Ver Detalles
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No hay tareas registradas</p>
              <button 
                className="btn btn-primary" 
                onClick={() => goToExternal('/admin/questions/task/t')}
              >
                Crear Primera Tarea
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default TasksList;
